import { useMemo } from 'react';
import { SPECIFICATION_KEYS } from '../constants';
import type { Category, CategorySpecification, MachineOption, SpecificationKey } from '../types';

interface UseCategoryLinkedOptionsParams {
  categories: Category[];
  options: MachineOption[];
  // Free-text mirror of the category field (e.g. form.category) — only used to resolve
  // categoryValue/categoryInputValue below, has no effect on the filtering logic.
  category: string;
  categoryId: string | null;
  selectedOptionIds: string[];
}

interface UseCategoryLinkedOptionsReturn {
  selectedCategory: Category | undefined;
  // Resolved value/inputValue pair for a freeSolo category Autocomplete — null (not '') when
  // nothing has been typed/selected yet, since an empty string is still a non-null value to
  // MUI's Autocomplete and would make it show its clear ("x") icon prematurely. categoryInputValue
  // drives the Autocomplete's controlled `inputValue` so the displayed text always matches the
  // resolved value rather than MUI's own post-selection echo — without this, a "+ Nieuwe
  // categorie toevoegen" row's own label gets stuck in the field if the user opens that dialog
  // and cancels without creating anything.
  categoryValue: Category | string | null;
  categoryInputValue: string;
  filteredCatalogOptions: MachineOption[];
  hasNoLinkedOptions: boolean;
  eligibleCategories: Category[];
  hasNoEligibleCategories: boolean;
  categorySpecs: CategorySpecification[];
  sortedCategorySpecKeys: SpecificationKey[];
}

// Shared category<->options linking/filtering behind the "Categorie" and "Opties / toebehoren"
// fields on both MachineFormPage (create) and DetailActionDialog (edit) — kept in one place so
// the two forms can't drift out of sync with each other.
export function useCategoryLinkedOptions({
  categories,
  options,
  category,
  categoryId,
  selectedOptionIds,
}: UseCategoryLinkedOptionsParams): UseCategoryLinkedOptionsReturn {
  const selectedCategory = useMemo(
    () => (categoryId ? categories.find((c) => c.id === categoryId) : undefined),
    [categories, categoryId]
  );

  const categoryValue: Category | string | null = categoryId
    ? (selectedCategory ?? { id: categoryId, name: category, organization_id: '', archived: false, created_at: '' })
    : (category || null);
  const categoryInputValue = typeof categoryValue === 'string' ? categoryValue : (categoryValue?.name ?? '');

  // A real (managed) category always restricts to its own linked options, even when that list
  // is empty — a category with nothing linked genuinely has zero applicable catalog options, it
  // is not "unconfigured" (there's no way to tell the two apart, and the UI treats them the
  // same: an empty picker + an explanatory message). Only a free-text category (no categoryId
  // at all) has no linking concept, so it still shows the full catalog. An already-selected
  // option always stays in the list too, regardless of the category's link config — this
  // matters right after quick-creating a new option linked to the current category: the
  // `categories` list isn't refetched after that link is written, so the category's own
  // option_ids would otherwise look briefly incompatible with the option it was just linked to.
  const filteredCatalogOptions = useMemo(
    () =>
      selectedCategory
        ? options.filter((o) => (selectedCategory.option_ids ?? []).includes(o.id) || selectedOptionIds.includes(o.id))
        : options,
    [options, selectedCategory, selectedOptionIds]
  );
  const hasNoLinkedOptions = !!selectedCategory && filteredCatalogOptions.length === 0;

  const categorySpecs = selectedCategory?.specifications ?? [];
  const sortedCategorySpecKeys = useMemo(
    () => SPECIFICATION_KEYS.filter((k) => (selectedCategory?.specifications ?? []).some((s) => s.key === k)),
    [selectedCategory]
  );

  // Reverse direction: once one or more managed (non-free-text) options are already picked, the
  // category picker narrows to categories compatible with all of them. Free-text options never
  // participate (they carry no category link), and an empty option_ids list excludes a category
  // from every match, same "empty means zero applicable" reasoning as filteredCatalogOptions.
  // The currently selected category always stays in the list too, for the same staleness reason
  // as filteredCatalogOptions above.
  const eligibleCategories = useMemo(
    () =>
      selectedOptionIds.length > 0
        ? categories.filter(
            (c) => c.id === categoryId || selectedOptionIds.every((oid) => (c.option_ids ?? []).includes(oid))
          )
        : categories,
    [categories, categoryId, selectedOptionIds]
  );
  const hasNoEligibleCategories = selectedOptionIds.length > 0 && eligibleCategories.length === 0;

  return {
    selectedCategory,
    categoryValue,
    categoryInputValue,
    filteredCatalogOptions,
    hasNoLinkedOptions,
    eligibleCategories,
    hasNoEligibleCategories,
    categorySpecs,
    sortedCategorySpecKeys,
  };
}
